import React from 'react';
import { Button } from 'primereact/button';
import { PrimeIcons } from 'primereact/api';
import './CustomPaginator.scss';

interface CustomPaginatorProps {
  page: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (newPage: number, newPageSize?: number) => void;
  pageSizeOptions: number[];
}

export const CustomPaginator = ({ page, pageSize, totalRecords, onPageChange, pageSizeOptions }: CustomPaginatorProps) => {
  const totalPages = Math.ceil(totalRecords / pageSize);
  const firstRecord = totalRecords === 0 ? 0 : page * pageSize + 1;
  const lastRecord = Math.min((page + 1) * pageSize, totalRecords);

  const handlePageSizeChange = (newPageSize: number) => {
    const newTotalPages = Math.ceil(totalRecords / newPageSize);
    const newPage = Math.min(page, newTotalPages - 1);
    onPageChange(Math.max(0, newPage), newPageSize);
  };

  // The four navigation buttons are disabled exactly when their move is not possible (page === 0, page >= totalPages - 1).
  const goToFirstPage = () => onPageChange(0);

  const goToPreviousPage = () => onPageChange(page - 1);

  const goToNextPage = () => onPageChange(page + 1);

  const goToLastPage = () => onPageChange(totalPages - 1);

  const goToPage = (targetPage: number) => {
    onPageChange(targetPage);
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page < 3) {
        for (let i = 0; i < 4; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages - 1);
      } else if (page > totalPages - 4) {
        pages.push(0);
        pages.push('ellipsis');
        for (let i = totalPages - 4; i < totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(0);
        pages.push('ellipsis');
        for (let i = page - 1; i <= page + 1; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages - 1);
      }
    }

    return pages;
  };

  if (totalRecords === 0) {
    return null;
  }

  return (
    <div className='custom-paginator'>
      <div className='paginator-info'>
        <span className='paginator-text'>
          Página <strong>{page + 1}</strong> de <strong>{totalPages}</strong>
        </span>
        <span className='paginator-text-mobile'>
          <strong>{page + 1}</strong> / <strong>{totalPages}</strong>
        </span>
        <span className='paginator-range'>
          Tickets <strong>{firstRecord}</strong> - <strong>{lastRecord}</strong> de <strong>{totalRecords}</strong>
        </span>
      </div>

      <div className='paginator-controls'>
        <div className='paginator-navigation'>
          <Button
            icon={PrimeIcons.ANGLE_DOUBLE_LEFT}
            onClick={goToFirstPage}
            disabled={page === 0}
            className='paginator-button'
            text
            rounded
            aria-label='Primera página'
          />
          <Button
            icon={PrimeIcons.ANGLE_LEFT}
            onClick={goToPreviousPage}
            disabled={page === 0}
            className='paginator-button'
            text
            rounded
            aria-label='Página anterior'
          />

          <div className='paginator-pages'>
            {getPageNumbers().map((pageNum, index) => {
              if (pageNum === 'ellipsis') {
                return (
                  <span key={`ellipsis-${index}`} className='paginator-ellipsis'>
                    ...
                  </span>
                );
              }
              const pageIndex = pageNum as number;
              return (
                <button
                  key={pageIndex}
                  className={`paginator-page ${pageIndex === page ? 'active' : ''}`}
                  onClick={() => goToPage(pageIndex)}
                  aria-label={`Ir a página ${pageIndex + 1}`}
                >
                  {pageIndex + 1}
                </button>
              );
            })}
          </div>

          <Button
            icon={PrimeIcons.ANGLE_RIGHT}
            onClick={goToNextPage}
            disabled={page >= totalPages - 1}
            className='paginator-button'
            text
            rounded
            aria-label='Página siguiente'
          />
          <Button
            icon={PrimeIcons.ANGLE_DOUBLE_RIGHT}
            onClick={goToLastPage}
            disabled={page >= totalPages - 1}
            className='paginator-button'
            text
            rounded
            aria-label='Última página'
          />
        </div>

        <div className='paginator-page-size'>
          <span className='page-size-label'>Tickets por página:</span>
          <select
            value={pageSize}
            onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
            className='page-size-select'
            aria-label='Tickets por página'
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};
